// First: in the assistant's hidden frame this makes window.fetch read-only before
// the line below captures it.
import "./embed-write-guard-install";
import { getBuildHash, markBuildStale } from "./build-version";
import { getGlobalHeadersAsObject } from "./global-headers";
import { handleApiError } from "./interceptors";

// Store the original fetch function
const originalFetch = window.fetch;

// Create a wrapped fetch function that includes our interceptors
const wrappedFetch = async function (
	input: RequestInfo | URL,
	init?: RequestInit
): Promise<Response> {
	const url = input instanceof Request ? input.url : input.toString();
	console.debug("[Fetch] Starting request", {
		url,
		method: init?.method,
		headers: init?.headers,
	});

	// Add default headers
	const headers = {
		...getGlobalHeadersAsObject(),
	};

	// Add any additional headers from init, but ensure Authorization is not duplicated
	if (init?.headers) {
		Object.entries(init.headers).forEach(([key, value]) => {
			if (key.toLowerCase() !== "authorization") {
				headers[key] = value;
			}
		});
	}

	const modifiedInit = {
		...init,
		headers: headers,
	};

	try {
		// Make the actual fetch call
		console.debug("[Fetch] Making request", {
			url,
			headers: modifiedInit.headers,
		});
		let response = await originalFetch(input, modifiedInit);

		console.debug("[Fetch] Received response", {
			url,
			status: response.status,
			ok: response.ok,
			headers: Object.fromEntries(response.headers.entries()),
		});

		// Detect stale frontend bundle: server stamps X-App-Version on every
		// response. If it diverges from the build-time hash, flip the global
		// stale flag once — banner + write-block read from it.
		// Browser-cacheable responses (max-age > 0, e.g. support attachment
		// downloads) are replayed with the version of the build that first
		// served them, so they say nothing about what the server runs now.
		const serverBuild = response.headers.get("X-App-Version");
		const clientBuild = getBuildHash();
		const maxAge = Number(
			/max-age=(\d+)/.exec(response.headers.get("Cache-Control") ?? "")?.[1] ?? 0
		);
		if (
			maxAge === 0 &&
			serverBuild &&
			clientBuild &&
			serverBuild !== "dev" &&
			clientBuild !== "dev" &&
			serverBuild !== clientBuild
		) {
			markBuildStale(serverBuild);
		}

		// Handle 401 Unauthorized
		if (response.status === 401) {
			console.debug("[Fetch] Handling 401 response", {
				url: response.url,
				headers: Object.fromEntries(response.headers.entries()),
			});

			// Skip token refresh for authentication endpoints
			// This prevents token refresh attempts during login and refresh
			if (
				response.url.includes("/api/v1/auth/refresh/") ||
				response.url.includes("/api/v2/auth/login/")
			) {
				console.warn(
					"[Fetch] Skipping token refresh for auth endpoint:",
					response.url
				);
				// For login endpoint, we want to preserve the original error
				let originalErrorMessage = "Authentication failed";

				try {
					const errorData = await response.clone().json();
					console.warn(
						"[Fetch] Auth endpoint error data:",
						JSON.stringify(errorData)
					);

					// Throw the original error from the backend
					if (errorData.error) {
						originalErrorMessage = errorData.error;
						console.warn(
							"[Fetch] Using backend error message:",
							originalErrorMessage
						);
					} else if (errorData.detail) {
						originalErrorMessage = errorData.detail;
						console.warn(
							"[Fetch] Using backend detail message:",
							originalErrorMessage
						);
					}
				} catch (parseError) {
					console.warn("[Fetch] Error parsing response:", parseError);
				}

				// Only clear tokens for refresh endpoint failures
				if (response.url.includes("/api/v1/auth/refresh/")) {
					console.debug("[Fetch] Clearing tokens due to refresh failure");
					localStorage.removeItem("access_token");
					localStorage.removeItem("refresh_token");

					if (!window.location.pathname.includes("/login")) {
						setTimeout(() => {
							window.location.href = "/login?session_expired=true";
						}, 100);
					}
				}

				console.warn(
					"[Fetch] Throwing error with message:",
					originalErrorMessage
				);
				throw new Error(originalErrorMessage);
			}

			try {
				return await handleApiError(response, () => {
					console.debug("[Fetch] Retrying request after token refresh", {
						url: response.url,
					});
					// Get fresh headers after token refresh
					const retryHeaders = {
						...getGlobalHeadersAsObject(), // This will include the new Authorization header
					};
					return wrappedFetch(response.url, {
						...modifiedInit,
						headers: retryHeaders,
					});
				});
			} catch (error) {
				console.debug("[Fetch] Token refresh failed, redirecting to login", {
					error,
				});

				// Any 401 that survived refresh is an auth failure — redirect to login.
				// Status code is the contract; message text is i18n-fragile and source-fragile
				// (SimpleJWT returns "Token is invalid", "Given token not valid for any token type",
				// "Token is expired" — none of which match a hardcoded English allowlist).
				const isAuthError = response.status === 401;

				if (isAuthError && !window.location.pathname.includes("/login")) {
					// Small delay to ensure any pending state updates complete
					setTimeout(() => {
						window.location.href = "/login?session_expired=true";
					}, 100);
				}

				// Still throw the error for the calling code to handle
				throw error;
			}
		}

		return response;
	} catch (error) {
		console.debug("[Fetch] Request failed", {
			url,
			error,
		});
		throw error;
	}
};

// Initialize fetch override
export function initializeFetchOverride() {
	console.debug("[Fetch] Initializing fetch override");
	// Carry fetch's own properties: where `typeof fetch` declares some
	// (bun's types add `preconnect`), a bare function is not a fetch.
	window.fetch = Object.assign(wrappedFetch, originalFetch);
	console.debug("[Fetch] Global fetch successfully overridden");
}

// Export the original fetch in case it's needed
export const fetchWithoutInterceptors = originalFetch;

// Override fetch immediately when this module is imported
initializeFetchOverride();

// Typed JSON helpers
export class ApiHttpError extends Error {
	status: number;
	url: string;
	data?: unknown;

	constructor(message: string, status: number, url: string, data?: unknown) {
		super(message);
		this.status = status;
		this.url = url;
		this.data = data;
		Object.setPrototypeOf(this, ApiHttpError.prototype);
	}
}

/** A 2xx response whose body never fully arrived: the connection dropped while
 * it was being read. Browsers reject that read with differing errors (Chrome
 * a `DOMException` named `NetworkError`, the spec a `TypeError`), so
 * `requestJson` wraps it in this one type. */
export class ApiNetworkError extends Error {
	url: string;

	constructor(url: string, readError: unknown) {
		super(`Response body from ${url} could not be read: ${String(readError)}`);
		this.url = url;
		Object.setPrototypeOf(this, ApiNetworkError.prototype);
	}
}

/** Gateway statuses traefik answers with while the containers behind a route
 * are being recreated. A prod deploy takes the stack down for 30-90 s, several
 * times a day, and every page left open polls straight through that window. */
const TRANSIENT_HTTP_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/** True only for a deploy-window gateway failure. A 403, a 500 or a fetch that
 * failed at the network layer is a real error with no benign explanation, and
 * stays visible. */
export function isTransientApiError(error: unknown): boolean {
	return error instanceof ApiHttpError && TRANSIENT_HTTP_STATUSES.has(error.status);
}

/** Messages browsers put on the `TypeError` that `fetch` rejects with when the
 * request never reached a server (offline, laptop waking from sleep, DNS or
 * connection failure): Chrome, Firefox and Safari respectively. */
const NETWORK_FETCH_ERROR_MESSAGES: ReadonlySet<string> = new Set([
	"Failed to fetch",
	"NetworkError when attempting to fetch resource.",
	"Load failed",
]);

/** True for a fetch that failed at the network layer. Only meant for polling
 * chains via `skipFailedPollTick`, where the next tick retries. The Sentry
 * `beforeSend` filter (`utils/sentry.ts`) deliberately does not use it, so a
 * one-off network failure outside a polling chain still reports. */
export function isNetworkFetchError(error: unknown): boolean {
	return (
		error instanceof ApiNetworkError ||
		(error instanceof TypeError && NETWORK_FETCH_ERROR_MESSAGES.has(error.message))
	);
}

/** `.catch` handler for a polling chain: skip a tick that failed because the
 * network was down (`isNetworkFetchError`) or a deploy window ate it
 * (`isTransientApiError`) — the next tick retries — and rethrow anything else.
 * For polling chains only: a one-off request has no next tick, so its network
 * failure must keep reporting. */
export function skipFailedPollTick(error: unknown): void {
	if (isTransientApiError(error) || isNetworkFetchError(error)) {
		return;
	}
	throw error;
}

/** `.catch` handler for a polling chain: skip the tick a deploy window ate —
 * the last data stays on screen and the next tick repopulates it — and rethrow
 * anything else so it surfaces exactly as it does today. */
export function skipTransientApiErrors(error: unknown): void {
	if (isTransientApiError(error)) {
		return;
	}
	throw error;
}

type ApiOptions = {
	headers?: Record<string, string>;
	signal?: AbortSignal;
	/** Lets the request outlive the page, for a call made as the user navigates away. */
	keepalive?: boolean;
};

async function parseJsonSafe(res: Response): Promise<unknown | undefined> {
	try {
		// 204 No Content or empty body should not throw
		const text = await res.clone().text();
		if (!text) return undefined;
		return JSON.parse(text);
	} catch (_e) {
		return undefined;
	}
}

/** What the server said went wrong, for a UI to show as-is.
 *
 * DRF answers in three shapes: `{"detail": "..."}` from an APIException,
 * `["..."]` from a ValidationError raised with a bare string, and
 * `{"field": ["..."]}` from a serializer. Only the first was read, so every
 * validation error reached the UI as "Bad Request" and its actual reason was
 * visible only in devtools.
 *
 * A gateway answers neither: traefik's 502 body is the bare string "Bad
 * Gateway" and HTTP/2 carries no statusText, so every such failure used to
 * read "Request failed" and collapsed into one undiagnosable Sentry issue.
 * The status goes in the fallback so they split by what actually happened. */
function errorMessage(data: unknown, response: Response): string {
	const fallback = response.statusText || `Request failed (${response.status})`;
	if (typeof data === "string") {
		return data || fallback;
	}
	if (Array.isArray(data)) {
		return data.map(String).join(" ") || fallback;
	}
	if (typeof data === "object" && data !== null) {
		const body = data as Record<string, unknown>;
		if ("detail" in body) {
			return String(body.detail) || fallback;
		}
		const fields = Object.entries(body)
			.map(([field, messages]) =>
				`${field}: ${[messages].flat().map(String).join(" ")}`.replace(
					/^non_field_errors: /,
					""
				)
			)
			.join("\n");
		return fields || fallback;
	}
	return fallback;
}

async function requestJson<TRes>(
	url: string,
	init: RequestInit,
	options?: ApiOptions
): Promise<TRes> {
	const headers = {
		...getGlobalHeadersAsObject(),
		...(options?.headers || {}),
	} as Record<string, string>;

	// Ensure JSON content-type when sending a body; a form post leaves it to the
	// browser, which adds the multipart boundary.
	if (init.body && !(init.body instanceof FormData) && !headers["Content-Type"]) {
		headers["Content-Type"] = "application/json";
	}

	const response = await fetch(url, {
		...init,
		headers,
		signal: options?.signal,
		keepalive: options?.keepalive,
	});
	if (!response.ok) {
		const data = await parseJsonSafe(response);
		throw new ApiHttpError(
			errorMessage(data, response),
			response.status,
			response.url,
			data
		);
	}
	// If no content, return undefined as any (caller should type accordingly)
	const data = await readJsonBody(response);
	return (data as TRes)!;
}

/** The body of a 2xx response. Unlike `parseJsonSafe` it does not swallow
 * failures: a dropped connection becomes `ApiNetworkError`, and a truncated or
 * non-JSON body throws its `SyntaxError` here rather than reaching the caller
 * as `undefined`. */
async function readJsonBody(res: Response): Promise<unknown | undefined> {
	let text: string;
	try {
		text = await res.text();
	} catch (error) {
		if (error instanceof DOMException && error.name === "AbortError") {
			throw error;
		}
		if (error instanceof TypeError || error instanceof DOMException) {
			throw new ApiNetworkError(res.url, error);
		}
		throw error;
	}
	if (!text) return undefined;
	return JSON.parse(text);
}

export function apiGet(url: string, options?: ApiOptions): Promise<unknown> {
	return requestJson<unknown>(url, { method: "GET" }, options);
}

export function apiPost(
	url: string,
	payload: unknown,
	options?: ApiOptions
): Promise<unknown> {
	return requestJson<unknown>(
		url,
		{ method: "POST", body: JSON.stringify(payload) },
		options
	);
}

/** POST a multipart form (a file upload). A failure throws an `ApiHttpError`
 * carrying the server's own reason, so a UI can show why the upload was refused. */
export function apiPostForm(
	url: string,
	form: FormData,
	options?: ApiOptions
): Promise<unknown> {
	return requestJson<unknown>(url, { method: "POST", body: form }, options);
}

export function apiPut(
	url: string,
	payload: unknown,
	options?: ApiOptions
): Promise<unknown> {
	return requestJson<unknown>(
		url,
		{ method: "PUT", body: JSON.stringify(payload) },
		options
	);
}

export function apiPatch(
	url: string,
	payload: unknown,
	options?: ApiOptions
): Promise<unknown> {
	return requestJson<unknown>(
		url,
		{ method: "PATCH", body: JSON.stringify(payload) },
		options
	);
}

export function apiDelete(url: string, options?: ApiOptions): Promise<unknown> {
	return requestJson<unknown>(url, { method: "DELETE" }, options);
}
